import { AccessTime } from "@mui/icons-material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { omitIvrMenuBusinessHoursField } from "./routePayloadNormalize";

const BusinessHours = ({
  route,
  setRoute,
  setSnackMsg,
  setOpenSnack,
  setLoader,
  enableBusinessHours,
  setEnableBusinessHours,
}) => {
  const navigate = useNavigate();
  const days = [
    { key: "mon", label: "Mon" },
    { key: "tue", label: "Tue" },
    { key: "wed", label: "Wed" },
    { key: "thur", label: "Thu" },
    { key: "fri", label: "Fri" },
    { key: "sat", label: "Sat" },
    { key: "sun", label: "Sun" },
  ];

  const handleTimeChange = (day, type, value) => {
    setRoute((prev) => {
      const prevDay = prev.calendar?.[day] || {};
      const startTime = (prevDay.earlyTime ?? prevDay.early_time) || "09:00";
      const endTime = (prevDay.lateTime ?? prevDay.late_time) || "17:00";

      // Compare times in minutes for simplicity
      const [newHours, newMinutes] = value.split(":").map(Number);
      const newTotal = newHours * 60 + newMinutes;

      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const startTotal = startHours * 60 + startMinutes;

      const [endHours, endMinutes] = endTime.split(":").map(Number);
      const endTotal = endHours * 60 + endMinutes;

      // Validation logic
      if (
        (type === "early" && newTotal >= endTotal) ||
        (type === "late" && newTotal <= startTotal)
      ) {
        setSnackMsg("End time must be later than start time.");
        setOpenSnack(true);
        return prev; // Don't update if invalid
      }

      return {
        ...prev,
        calendar: {
          ...prev.calendar,
          [day]: {
            ...prevDay,
            [type === "early" ? "earlyTime" : "lateTime"]: value,
          },
        },
      };
    });
  };

  const handleToggle = (day, checked) => {
    setRoute((prev) => ({
      ...prev,
      calendar: {
        ...prev.calendar,
        [day]: {
          ...prev.calendar?.[day],
          active: checked,
        },
      },
    }));
  };

  const orgData = JSON.parse(localStorage.getItem("organization") || "{}");

  return (
    <div className="border-2 border-gray-200 rounded-xl p-6 bg-white lg:w-[740px] w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start space-x-3">
          <AccessTime className="text-orange-500 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Business Hours
            </h2>
            <p className="text-sm text-gray-600">
              Separate daytime and after-hours call handling
            </p>
          </div>
        </div>

        <div
          onClick={() => {
            if (!enableBusinessHours) {
              // Turning ON route-level business hours
              const ivrOptions = route?.ivrMenu?.options ?? {};
              const hasOptionLevelBusinessHours = Object.values(ivrOptions).some((opt) => {
                if (!opt || typeof opt !== "object") return false;
                const hasCalendar = !!opt.calendar;
                const hasBusinessHoursFlag = opt.businessHoursEnabled === true;
                const hasOffHoursConfig =
                  !!opt.offHoursAction || !!opt.offHoursNotification || !!opt.offHoursChannel;
                return hasCalendar || hasBusinessHoursFlag || hasOffHoursConfig;
              });

              if (hasOptionLevelBusinessHours) {
                const confirmed = window.confirm(
                  "Enabling route-level Business Hours will remove per-option Business Hours and their after-hours actions for IVR menu options. Do you want to continue?"
                );
                if (!confirmed) {
                  return;
                }
              }

              const ivrOptionsCleared = Object.entries(ivrOptions).length
                ? Object.fromEntries(
                    Object.entries(ivrOptions).map(([key, opt]) => [
                      key,
                      {
                        ...opt,
                        businessHoursEnabled: false,
                        calendar: null,
                        offHoursAction: null,
                        offHoursNotification: null,
                        offHoursChannel: null,
                      },
                    ])
                  )
                : ivrOptions;

              const cleared = {
                ...route,
                businessHoursEnabled: true,
                calendar: {},
                ivrMenu: route.ivrMenu
                  ? {
                      ...omitIvrMenuBusinessHoursField(route.ivrMenu),
                      options: ivrOptionsCleared,
                    }
                  : route.ivrMenu,
              };

              setRoute(cleared);
              setEnableBusinessHours(true);
            } else {
              // Turning OFF route-level business hours: clear route-level after-hours config
              setEnableBusinessHours(false);
              const cleared = {
                ...route,
                businessHoursEnabled: false,
                calendar: {},
                offHoursAction: null,
                offHoursNumber: null,
                offHoursRecording: null,
                offHoursAgent: null,
                offHoursChannel: null,
                offHoursNotification: null,
                offHoursIvrMenu: null,
              };
              setRoute(cleared);
            }
          }}
          className={`${
            enableBusinessHours
              ? "bg-orange-100 text-orange-700 font-bold"
              : "bg-gray-100 text-gray-700 font-medium"
          } text-sm py-1.5 px-4 rounded-lg cursor-pointer`}
        >
          {enableBusinessHours ? "Enabled" : "Disabled"}
        </div>
      </div>

      {enableBusinessHours && (
        <div className="py-5 bg-white">
          <div className="text-[16px] font-medium text-gray-800 mb-4">
            Weekly Schedule
          </div>
          {/* Days List */}
          <div className="rounded-lg border border-gray-200 divide-y">
            {days.map(({ key, label }) => {
              const isActive = route?.calendar?.[key]?.active || false;
              const startTime = (route?.calendar?.[key]?.earlyTime ?? route?.calendar?.[key]?.early_time) || "09:00";
              const endTime = (route?.calendar?.[key]?.lateTime ?? route?.calendar?.[key]?.late_time) || "17:00";

              return (
                <div key={key} className="p-3 flex items-center gap-3">
                  {/* Day Label */}
                  <div className="w-14 text-sm text-gray-700">{label}</div>
                  
                  {/* Status Badge and Time Inputs */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggle(key, !isActive)}
                      className={`px-2 py-1 text-xs rounded-md border ${
                        isActive
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-gray-50 text-gray-600 border-gray-200"
                      } hover:bg-gray-100`}
                    >
                      {isActive ? "Open" : "Closed"}
                    </button>
                    {isActive && (
                      <>
                        <input
                          type="time"
                          className="text-sm rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          style={{ 
                            paddingRight: '0.5rem',
                            paddingLeft: '0.5rem',
                            width: '7rem',
                            minWidth: '7rem',
                            marginRight: '0.5rem'
                          }}
                          value={startTime}
                          onChange={(e) => handleTimeChange(key, "early", e.target.value)}
                        />
                        <span className="text-gray-400 mx-1">to</span>
                        <input
                          type="time"
                          className="text-sm rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          style={{ 
                            paddingRight: '0.5rem',
                            paddingLeft: '0.5rem',
                            width: '7rem',
                            minWidth: '7rem',
                            marginLeft: '0.5rem'
                          }}
                          value={endTime}
                          onChange={(e) => handleTimeChange(key, "late", e.target.value)}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Apply Buttons */}
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Quick apply:</span>

            {/* Mon–Fri 9–5 */}
            <button
              className="px-3 py-1 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
              onClick={() => {
                const updatedRoute = {
                  ...route,
                  calendar: { ...route.calendar },
                };
                ["mon", "tue", "wed", "thur", "fri"].forEach((day) => {
                  updatedRoute.calendar[day] = {
                    ...updatedRoute.calendar[day],
                    active: true,
                    earlyTime: "09:00",
                    lateTime: "17:00",
                  };
                });
                ["sat", "sun"].forEach((day) => {
                  updatedRoute.calendar[day] = {
                    ...updatedRoute.calendar[day],
                    active: false,
                  };
                });
                setRoute(updatedRoute);
              }}
            >
              Mon–Fri 9:00–17:00
            </button>

            {/* Weekend Closed */}
            <button
              className="px-3 py-1 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
              onClick={() => {
                const updatedRoute = {
                  ...route,
                  calendar: { ...route.calendar },
                };
                // Set Monday-Friday to be open all day (00:00 to 23:59)
                ["mon", "tue", "wed", "thur", "fri"].forEach((day) => {
                  updatedRoute.calendar[day] = {
                    ...updatedRoute.calendar[day],
                    active: true,
                    earlyTime: "00:00",
                    lateTime: "23:59",
                  };
                });
                // Set Saturday and Sunday to be closed
                ["sat", "sun"].forEach((day) => {
                  updatedRoute.calendar[day] = {
                    ...updatedRoute.calendar[day],
                    active: false,
                  };
                });
                setRoute(updatedRoute);
              }}
            >
              Weekend Closed
            </button>

            {/* Clear All */}
            <button
              className="px-3 py-1 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
              onClick={() => {
                const cleared = { ...route, calendar: {} };
                setRoute(cleared);
              }}
            >
              Clear All
            </button>
          </div>

          {/* Timezone Info */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => navigate("/dashboard/settings/organization")}
              className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-100 hover:text-gray-800 cursor-pointer"
              title="Timezone is configured in Organization Settings"
            >
              <span className="mr-2 text-gray-400">Timezone:</span>
              <span className="text-gray-800">{orgData?.org_timezone?.S || "Not set"}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessHours;
